import { Expense, RightEnum, Vendor, VendorTypeEnum } from "@finway-group/shared/lib/models"
import { Tooltip } from "antd"
import React, { useState } from "react"
import { AlertTriangle } from "react-feather"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { Link } from "react-router-dom"

import { patchExpense } from "Shared/store/actions/expense/expenseActions"

import DeletedTooltip from "../../../../../components/deletedTooltip"
import { VendorSelect } from "../../../../../components/forms/expenseForm/commonInputFields/VendorSelect"
import { useCompany } from "../../../../../shared/hooks/company.hooks"
import { useEmployees } from "../../../../../shared/hooks/employee.hooks"
import { useUserCreditors } from "../../../../../shared/hooks/vendor.hooks"
import { RoutePathEnum } from "../../../../../shared/router/RoutePath.enum"
import { ApprovalProcessService, AuthzService, NotificationService } from "../../../../../shared/services"
import { buildUpdateDataAccordingToSetting, isHospitalityOrStandardReimbursement } from "../../../../../shared/utils/expense.utils"
import { getTooltipPopupContainer } from "../../../../../shared/utils/helper.utils"
import { UserCreditor } from "../../../../../shared/utils/vendor.utils"

interface VendorFieldProps {
    expense: Expense
    setIsExpenseUpdating?: React.Dispatch<React.SetStateAction<boolean>>
    setSelectedExpense?: (expense: Expense | undefined) => void
    currentExpenseCreditor: UserCreditor | Vendor | undefined
    vendorCreditor?: Vendor
    isReadOnly?: boolean
    areInputsDisabled: boolean
}

export const VendorField = ({ expense, currentExpenseCreditor, isReadOnly, vendorCreditor, setIsExpenseUpdating, areInputsDisabled, setSelectedExpense }: VendorFieldProps) => {
    const { t } = useTranslation()
    const dispatch = useDispatch()

    const isUserCreditor = expense?.vendorType === VendorTypeEnum.USER
    const embeddedExpenseCreditor = expense.getCreditor()
    const company = useCompany()
    const userCreditors = useUserCreditors(AuthzService.isRightGrantedForLoggedInUser(RightEnum.EMPLOYEE__ALL__READ))
    const employees = useEmployees({ excludeDeleted: true })

    const [isVendorUpdating, setIsVendorUpdating] = useState(false)

    const onVendorUpdate = async (value: string, vendorType: VendorTypeEnum = VendorTypeEnum.VENDOR) => {
        try {
            setIsVendorUpdating(true)
            setIsExpenseUpdating?.(true)

            // Assign new vendor value to the expense object
            const updatedExpense = new Expense({
                ...expense,
                ...(vendorType === VendorTypeEnum.USER
                    ? {
                          vendorType: VendorTypeEnum.USER,
                          creditorUser: { _id: value },
                          vendor: undefined,
                      }
                    : {
                          vendorType: VendorTypeEnum.VENDOR,
                          vendor: { _id: value },
                          creditorUser: undefined,
                      }),
                skipEmail: true,
            })

            // Determine the cc, cc2, expenseaccount, etc, based on the newly updated expense
            const extraUpdateData = buildUpdateDataAccordingToSetting(updatedExpense, employees, company, vendorCreditor)
            Object.assign(updatedExpense, extraUpdateData)

            // Rebuild approval process with vendor change. Expenses inside folder do not change its aproval processes and follow the folder approval processes.
            if (!updatedExpense.folderId) {
                const approvalProcesses = await ApprovalProcessService.getApprovalProcessesAfterExpenseUpdate(updatedExpense, company, {
                    valuesChange: { vendor: { previousValue: expense.vendor?._id, currentValue: value } },
                    awaitConfirmation: true,
                })
                if (!approvalProcesses) {
                    return
                }
                updatedExpense.approvalProcesses = approvalProcesses
            }

            const patchPayload = {
                vendor: updatedExpense.vendor,
                vendorType: updatedExpense.vendorType,
                creditorUser: updatedExpense.creditorUser,
                approvalProcesses: updatedExpense.approvalProcesses,
                ...extraUpdateData,
            }

            const updatedExpenseResponse = await patchExpense(expense.id, patchPayload)(dispatch)
            setSelectedExpense?.(updatedExpenseResponse)
        } catch (err) {
            NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:error"))
        } finally {
            setIsVendorUpdating(false)
            setIsExpenseUpdating?.(false)
        }
    }

    const VendorDetails = () => {
        if (!currentExpenseCreditor || !embeddedExpenseCreditor) {
            return null
        }
        // early return if user has no vendor view right
        if (!AuthzService.isRightGrantedForLoggedInUser(RightEnum.VENDOR__ALL__READ)) {
            return <span className="text">{embeddedExpenseCreditor.name}</span>
        }

        const redirectDestination = isUserCreditor ? RoutePathEnum.EMPLOYEES : RoutePathEnum.VENDORS
        const VendorLink = (
            <Link className="block truncate max-w-full" to={`${redirectDestination}/${currentExpenseCreditor.id}`}>
                {embeddedExpenseCreditor.name}
            </Link>
        )
        return (
            <div className="min-w-0 max-w-full">
                {embeddedExpenseCreditor.creditorNumber ? (
                    <>
                        {VendorLink}
                        {currentExpenseCreditor.deleted && <DeletedTooltip translationKey="tooltips:vendor.was_deleted" deletionDate={currentExpenseCreditor?.dateDeleted} />}
                    </>
                ) : (
                    <Tooltip getPopupContainer={getTooltipPopupContainer} title={t("info:employee_no_creditor_number")}>
                        <div className="flex justify-end min-w-0">
                            <AlertTriangle className="px-2 mt-2 flex-shrink-0" fill="#FBAC3B" size={18} stroke="#FFFFFF" />
                            {VendorLink}
                        </div>
                    </Tooltip>
                )}
            </div>
        )
    }

    return (
        <li>
            <span className="title">{t("label:vendor")}</span>
            {currentExpenseCreditor && (isUserCreditor || (currentExpenseCreditor as Vendor)?.isApproved) ? (
                <VendorDetails />
            ) : !isReadOnly ? (
                <span className="w-1/2">
                    <VendorSelect
                        shouldShowOnlyVerifiedVendors={false}
                        shouldRenderSelectDropdown={isHospitalityOrStandardReimbursement(expense)}
                        selectedVendorType={expense?.vendorType === VendorTypeEnum.USER && currentExpenseCreditor ? VendorTypeEnum.USER : VendorTypeEnum.VENDOR}
                        userCreditors={userCreditors}
                        selectedCreditorId={currentExpenseCreditor ? currentExpenseCreditor.id : ""}
                        onSelect={onVendorUpdate}
                        disabled={isVendorUpdating || areInputsDisabled}
                        isLoading={isVendorUpdating}
                        defaultNonSelectedValue={true}
                        popupClassName="min-w-200"
                    />
                </span>
            ) : (
                <span className="text truncate">-</span>
            )}
        </li>
    )
}
